// @flow

import React from 'react'
import { useHistory } from 'react-router-dom'
import type { Message } from 'types/message'
import type { PacketResponse } from 'types/packet-response'
import type { Response } from 'types/response'

function useResponseOfficeUse(
  response: ?Response,
  packetResponse: ?PacketResponse,
  message: ?Message
): [
  boolean,
  {
    title: string,
    onClick: () => void,
  }
] {
  const history = useHistory()

  const responseHasOfficeUse = React.useMemo(() => {
    if (!response) return false

    const form = response?.form
    const document = response?.document

    return Boolean(
      message?.id &&
        (form?.has_office_use_questions || document?.has_office_use_questions)
    )
  }, [message?.id, response])

  const packetResponseHasOfficeUse = React.useMemo(() => {
    if (!packetResponse) return false

    return packetResponse.responses?.some(
      (response) => response.form?.has_office_use_questions
    )
  }, [packetResponse])

  const hasOfficeUse = React.useMemo(() => {
    return responseHasOfficeUse || packetResponseHasOfficeUse
  }, [packetResponseHasOfficeUse, responseHasOfficeUse])

  const navigateToResponseOfficeUse = React.useCallback(() => {
    if (!response) return

    const fwdQuery = message?.id ? `?message?.id=${message?.id}` : ''

    if (Boolean(response?.document)) {
      history.push(`/pdf/${response?.id ?? ''}/office-use${fwdQuery}`)
      return
    }

    history.push(`/fillFormOfficeUseQuestions/${response?.id ?? ''}${fwdQuery}`)
  }, [history, message?.id, response])

  const navigateToPacketResponseOfficeUse = React.useCallback(() => {
    if (!packetResponse) return

    const done = Boolean(packetResponse?.is_submitted)
    history.push(
      `/fillPacketOfficeUseQuestions/${
        packetResponse.id
      }?done=${done.toString()}`
    )
  }, [history, packetResponse])

  const onNavigateToOfficeUse = React.useCallback(() => {
    if (response) {
      navigateToResponseOfficeUse()
      return
    }

    if (packetResponse) {
      navigateToPacketResponseOfficeUse()
    }
  }, [
    response,
    packetResponse,
    navigateToResponseOfficeUse,
    navigateToPacketResponseOfficeUse,
  ])

  return [
    hasOfficeUse,
    {
      title: 'Complete Office Use',
      onClick: onNavigateToOfficeUse,
    },
  ]
}

export default useResponseOfficeUse
